import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { getBpoStatusAPI } from 'api/bpo'
import bpoData from 'utilities/bpoData'

interface BpoStatusState {
  status: string
  message: string
  progress: number
  estimatedTime: string
  isLoading: boolean
  requestId: number | null
  createdAt: Date | null
  bpoVerificationId: string | null
  originalVerificationId: string | null
}

/**
 * BPO審査状況を管理するカスタムフック
 * 
 * 【フック概要】
 * BPO（Business Process Outsourcing）人的審査の状態を監視するカスタムフック
 * AI判定に失敗した本人確認書類の人的審査状態を定期的にポーリングして取得
 * 
 * 【主な機能】
 * - 審査状態の定期ポーリング（デフォルト5分間隔）
 * - 審査進捗率の計算と表示
 * - 推定完了時間の動的計算
 * - 3時間タイムアウト監視
 * - 審査完了時の自動画面遷移
 * - エラーハンドリング（404、タイムアウト等）
 * 
 * 【状態遷移】
 * - pending → processing → approved/rejected
 * - タイムアウト: 3時間経過で自動的にtimeout状態へ
 * - エラー: API通信エラー時の適切なフォールバック
 * 
 * 【リファクタリング内容（2025-07-22）】
 * - BpoProcessingコンポーネントから状態管理ロジックを抽出
 * - ポーリング制御の最適化（useEffectのクリーンアップ改善）
 * - タイムアウト処理の追加（3時間制限）
 * - セッションストレージとの連携強化
 * - TypeScript型定義の強化（BpoStatusState）
 * 
 * @param pollingInterval ポーリング間隔（ミリ秒、デフォルト5分）
 * @returns {Object} BPO状態管理用のオブジェクト
 * - status: 現在のBPO審査状態
 * - message: ユーザー向けメッセージ
 * - progress: 進捗率（0-100）
 * - estimatedTime: 推定完了時間テキスト
 * - isLoading: API通信中フラグ
 * - requestId: BPO審査リクエストID
 * - setRequestId: リクエストIDを設定してポーリング開始
 * - checkBpoStatus: 手動で状態チェック
 * - stopPolling: ポーリング停止
 */
export const useBpoStatus = (pollingInterval: number = 5 * 60 * 1000) => {
  const navigate = useNavigate()
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const createdAtRef = useRef<Date | null>(null)
  const navigationTimerRef = useRef<NodeJS.Timeout | null>(null)
  
  const [state, setState] = useState<BpoStatusState>({
    status: 'pending',
    message: '審査準備中です',
    progress: 0,
    estimatedTime: '15～30分程度（最大3時間）',
    isLoading: false,
    requestId: null,
    createdAt: null,
    bpoVerificationId: null,
    originalVerificationId: null
  })

  // createdAtの初期化（sessionStorageから復元）
  useEffect(() => {
    const savedCreatedAt = bpoData.getItem('bpo_created_at')
    if (savedCreatedAt) {
      const createdDate = new Date(savedCreatedAt)
      createdAtRef.current = createdDate
      setState(prev => ({ ...prev, createdAt: createdDate }))
    }
  }, [])

  /**
   * ナビゲーションタイマーをセットする関数
   */
  const setNavigationTimer = useCallback((callback: () => void, delay: number) => {
    // 既存のタイマーがあればクリア
    if (navigationTimerRef.current) {
      clearTimeout(navigationTimerRef.current)
    }

    navigationTimerRef.current = setTimeout(() => {
      callback()
      navigationTimerRef.current = null
    }, delay)
  }, [])

  /**
   * タイムアウトチェック（3時間）
   */
  const checkTimeout = useCallback((createdTime: Date): boolean => {
    const now = new Date()
    const diffMs = now.getTime() - createdTime.getTime()
    const diffHours = diffMs / (1000 * 60 * 60)
    return diffHours >= 3
  }, [])

  /**
   * BPO審査状況をチェックする
   */
  const checkBpoStatus = useCallback(async (requestId: number) => {
    setState(prev => ({ ...prev, isLoading: true }))
    
    try {
      const response = await getBpoStatusAPI(requestId)
      
      if (response && response.status) {
        const { 
          status: bpoStatus, 
          message: bpoMessage, 
          progress: bpoProgress, 
          estimatedCompletionTime, 
          createdAt: responseCreatedAt,
          bpoVerificationId: responseBpoVerificationId,
          originalVerificationId: responseOriginalVerificationId
        } = response
        
        // 作成日時を保存（レスポンスから取得した日時を常に使用）
        let currentCreatedAt = createdAtRef.current
        if (responseCreatedAt) {
          currentCreatedAt = new Date(responseCreatedAt)
          // 初回またはcreatedAtが変わった場合のみ更新
          if (!createdAtRef.current || createdAtRef.current.getTime() !== currentCreatedAt.getTime()) {
            createdAtRef.current = currentCreatedAt
            bpoData.setItem('bpo_created_at', responseCreatedAt)
          }
        }
        
        // タイムアウトチェック
        if (currentCreatedAt && checkTimeout(currentCreatedAt)) {
          setState(prev => ({
            ...prev,
            status: 'timeout',
            message: '審査がタイムアウトしました。お問い合わせください。',
            isLoading: false
          }))
          bpoData.setBpoStatus('timeout')
          setNavigationTimer(() => {
            navigate('/bpo/error-response?action=timeout')
          }, 2000)
          return
        }
        
        // 推定完了時間の計算
        let estimatedTimeText = '15～30分程度（最大3時間）'
        if (estimatedCompletionTime) {
          const estimatedDate = new Date(estimatedCompletionTime)
          const now = new Date()
          const diffMinutes = Math.max(0, Math.ceil((estimatedDate.getTime() - now.getTime()) / (1000 * 60)))
          estimatedTimeText = diffMinutes > 0 ? `あと約${diffMinutes}分` : '間もなく完了予定'
        }
        
        setState(prev => ({
          ...prev,
          status: bpoStatus,
          message: bpoMessage,
          progress: bpoProgress,
          estimatedTime: estimatedTimeText,
          isLoading: false,
          createdAt: currentCreatedAt,
          bpoVerificationId: responseBpoVerificationId || prev.bpoVerificationId,
          originalVerificationId: responseOriginalVerificationId || prev.originalVerificationId
        }))
        
        // セッションストレージにステータス保存
        bpoData.setBpoStatus(bpoStatus)
        
        // 完了状態の場合は自動遷移
        if (bpoStatus === 'approved') {
          setNavigationTimer(() => {
            bpoData.clearAll()
            navigate('/registerIdentificationForIndividual')
          }, 2000)
        } else if (bpoStatus === 'rejected') {
          setNavigationTimer(() => {
            navigate('/bpo/rejected')
          }, 2000)
        }
      }
    } catch (error: any) {
      setState(prev => ({ ...prev, isLoading: false }))
      
      // 404エラー（不正なrequestId）の場合
      if (error?.response?.status === 404) {
        setState(prev => ({
          ...prev,
          message: '審査情報が見つかりません。最初からやり直してください。'
        }))
        setNavigationTimer(() => {
          bpoData.clearAll()
          navigate('/registerIdentificationForIndividual')
        }, 3000)
      } else {
        setState(prev => ({
          ...prev,
          message: 'エラーが発生しました。しばらくしてから再度お試しください。'
        }))
      }
    }
  }, [navigate, checkTimeout, setNavigationTimer])

  /**
   * ポーリングを開始する
   */
  const startPolling = useCallback((requestId: number) => {
    // 初回チェック
    checkBpoStatus(requestId)
    
    // 定期的なポーリング
    intervalRef.current = setInterval(() => {
      checkBpoStatus(requestId)
    }, pollingInterval)
  }, [checkBpoStatus, pollingInterval])

  /**
   * ポーリングを停止する
   */
  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  /**
   * リクエストIDを設定して監視を開始
   */
  const setRequestId = useCallback((requestId: number) => {
    setState(prev => ({ ...prev, requestId }))
    startPolling(requestId)
  }, [startPolling])

  // クリーンアップ
  useEffect(() => {
    return () => {
      stopPolling()
      // ナビゲーションタイマーのクリア
      if (navigationTimerRef.current) {
        clearTimeout(navigationTimerRef.current)
        navigationTimerRef.current = null
      }
    }
  }, [stopPolling])

  return {
    ...state,
    setRequestId,
    checkBpoStatus,
    stopPolling
  }
}